import { Button, Image, LibCombobox, lucideIcons, Skeleton, useComboboxAnchor } from '@round/creatorbase-ui';
import { SpotifyTrackItem, useSpotifyTrackCombobox } from './useSpotifyTrackCombobox';
import styles from './SpotifyTrackFilter.module.css';
import { useSelectedSpotifyTrackItems } from './useSelectedSpotifyTracks';
import cn from 'classnames';

const { X } = lucideIcons;

type Props = {
    selectedSongSpotifyIds: string[];
    onChange: (songSpotifyIds: string[]) => void;
};

const TrackItemLabel = ({ item, isLoading }: { item: Partial<SpotifyTrackItem> | undefined; isLoading?: boolean }) => (
    <div className={styles.labelContainer}>
        <Image src={item?.image_url || ''} className={styles.thumbnail} isLoading={isLoading} />
        <div className={styles.itemLabels}>
            {isLoading ? (
                <>
                    <Skeleton className={styles.labelSkeleton} />
                    <Skeleton className={styles.labelSkeleton} />
                </>
            ) : (
                <>
                    <span className={styles.itemTitle}>{item?.label || item?.value}</span>
                    <span className={styles.itemArtists}>{item?.artists?.map((a) => a.name).join(', ')}</span>
                </>
            )}
        </div>
    </div>
);

export function SpotifyTrackFilter({ selectedSongSpotifyIds, onChange }: Props) {
    const { items: value, isFetching: areSelectedTracksFetching } =
        useSelectedSpotifyTrackItems(selectedSongSpotifyIds);

    const { items, isFetching, isError, isSearching, filter, onInputValueChange, onItemsListScroll } =
        useSpotifyTrackCombobox();

    const anchor = useComboboxAnchor();

    const noItemsFound = isError
        ? 'Failed to load songs'
        : isFetching
          ? 'Loading...'
          : isSearching
            ? 'No songs found'
            : 'Type to search songs...';

    const handleSelect = (selected: SpotifyTrackItem[] | null) => {
        onChange((selected ?? []).map((t) => t.value));
        onInputValueChange('');
    };

    const handleRemove = (spotifyId: string) => {
        onChange(selectedSongSpotifyIds.filter((id) => id !== spotifyId));
    };

    return (
        <div className={styles.container}>
            <LibCombobox
                multiple
                value={value}
                onInputValueChange={onInputValueChange}
                debounce={500}
                items={items}
                isItemEqualToValue={(a, b) => a?.value === b?.value}
                onValueChange={handleSelect}
                filter={filter}
            >
                <div ref={anchor}>
                    <LibCombobox.Input placeholder="Search Spotify songs..." showTrigger={false} />
                </div>
                <LibCombobox.Content anchor={anchor} className={cn({ [styles.forceContentHeight]: !!items.length })}>
                    <LibCombobox.Empty>{noItemsFound}</LibCombobox.Empty>
                    <LibCombobox.List onScroll={onItemsListScroll}>
                        {(item: SpotifyTrackItem) => (
                            <LibCombobox.Item key={item.value} value={item}>
                                <TrackItemLabel item={item} />
                            </LibCombobox.Item>
                        )}
                    </LibCombobox.List>
                </LibCombobox.Content>
            </LibCombobox>

            {selectedSongSpotifyIds.length > 0 && (
                <div className={styles.selectedList} role="list">
                    {selectedSongSpotifyIds.map((id, i) => {
                        const track = value.find((track) => track.value === id);
                        const isLoading = !track && areSelectedTracksFetching;
                        return (
                            <div key={track?.value || i} className={styles.selectedItem} role="listitem">
                                {/*
                                    We can just show the spotify ID if no data was found.
                                    This case is expected if we haven't saved the track to our DB yet
                                    (After searching via the spotify API, we'll queue a task to save the data)
                                */}
                                <TrackItemLabel item={track || { value: id }} isLoading={isLoading} />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={styles.removeButton}
                                    disabled={isLoading}
                                    onClick={() => track && handleRemove(track.value)}
                                >
                                    <X />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
